import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { ref, set, get } from 'firebase/database';
import { database } from '../../firebase';
import CalendarIntegration from '../CalendarIntegration';
import { CALENDAR_ENABLED } from '../../utils/featureFlags';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

// URL of backend (adjust for production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const AddSubstagePopup = ({ substageType, onClose, currActiveStage, onSubstageAdded, userUID }) => {
  useUiLanguage();
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [substageName, setSubstageName] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [afterSubstageId, setAfterSubstageId] = useState('');
  const [existingSubstages, setExistingSubstages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [numQuestions, setNumQuestions] = useState(5);
  const [config, setConfig] = useState({});                 // NEW – extra settings per type
  const [showCalendarIntegration, setShowCalendarIntegration] = useState(false);


  /* ──────────────────────────────────────────────────────
   * Load existing substages (for "insert-after" dropdown)
   * ──────────────────────────────────────────────────── */
  useEffect(() => {
    const loadSubstagesForStage = async () => {
      if (substageType !== 'ai-assessment' || !userUID) return;
      try {
        const snapshot = await get(
          ref(database, `companies/${userUID}/hiringWorkflow/${currActiveStage}/substages`)
        );
        if (snapshot.exists()) {
          const list = Object.values(snapshot.val());
          setExistingSubstages(list);
        }
      } catch (err) {
        console.error('Error loading substages for dropdown:', err);
      }
    };
    loadSubstagesForStage();
  }, [substageType, userUID, currActiveStage]);

  /* ──────────────────────────────────────────────────────
   * Static lookup tables
   * ──────────────────────────────────────────────────── */
  const substageDetailsMap = {
    interview: { title: 'Interview', icon: 'fa-calendar-day' },
    assessment: { title: 'Assessment', icon: 'fa-gears' },
    'ai-assessment': { title: 'AI Assessment', icon: 'fa-robot' },
    filtering: { title: 'AI Filtering', icon: 'fa-filter' }
  };

  const assessmentOptions = [
    { value: 'sjt', label: 'Situational Judgment Test - Workplace Ethics & Decision Making' },
    { value: 'work_sample', label: 'Work Sample Challenge - Real Job Task Simulation' },
    { value: 'data_insight', label: 'Data Analysis Challenge - Business Intelligence & Analytics' },
    { value: 'video_pitch', label: 'Video Presentation - Communication & Pitch Skills' },
    { value: 'mcq_core', label: 'Multiple Choice Test - Industry Knowledge & Compliance' },
    { value: 'coding_sandbox', label: 'Programming Challenge - Technical Coding Assessment (Coming Soon!)', disabled: true },
    { value: 'personality', label: 'Personality Assessment - Work Style & Personal Traits' },
    { value: 'behavioral', label: 'Behavioral Assessment - Leadership & Teamwork Competencies' },
    { value: 'cognitive', label: 'Cognitive Assessment - Logical Reasoning & Problem Solving' },
    { value: 'mixed', label: 'Custom Mixed Assessment - Combine Multiple Assessment Types' }
  ];

  const substageInfo = substageDetailsMap[substageType] || { title: 'Substage', icon: 'fa-plus-square' };

  /* ──────────────────────────────────────────────────────
   * Helpers to update config object
   * ──────────────────────────────────────────────────── */
  const updateConfig = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  /* ──────────────────────────────────────────────────────
   * Save button handler
   * ──────────────────────────────────────────────────── */
  const handleSave = async () => {
    // Auto-set name for specific substage types
    let finalSubstageName = substageName;
    if (substageType === 'meet-the-team-and-assign-buddy' && !substageName.trim()) {
      finalSubstageName = 'Meet the Team';
      setSubstageName(finalSubstageName);
    } else if (substageType === 'first-week-checklist' && !substageName.trim()) {
      finalSubstageName = 'First Week Checklist';
      setSubstageName(finalSubstageName);
    }

    if (!finalSubstageName.trim()) {
      alert(uiText('Please enter a substage name'));
      return;
    }
    try {
      const newSubstageData = await saveNewSubstage(finalSubstageName);
      if (onSubstageAdded && newSubstageData) {
        onSubstageAdded(getCurrentStage(), newSubstageData);
      }

      // Check if this is the first interview substage and trigger calendar integration
      if (CALENDAR_ENABLED && substageType === 'interview') {
        await checkAndPromptCalendarIntegration();
      }

      onClose();
    } catch (error) {
      console.error('Error saving new substage:', error);
      alert(uiText('Error saving substage. Please try again.'));
    }
  };

  /* ──────────────────────────────────────────────────────
   * Check for existing calendar integration and prompt if needed
   * ──────────────────────────────────────────────────── */
  const checkAndPromptCalendarIntegration = async () => {
    if (!CALENDAR_ENABLED) return;
    try {
      // Check if user already has calendar integration
      const calendarRef = ref(database, `companies/${userUID}/calendarIntegrations`);
      const snapshot = await get(calendarRef);

      if (!snapshot.exists() || Object.keys(snapshot.val()).length === 0) {
        // No calendar integration found, show integration popup
        const shouldConnect = window.confirm(
          'You\'ve added your first interview substage! Would you like to connect your calendar for automatic interview scheduling and email notifications to job seekers?'
        );

        if (shouldConnect) {
          setShowCalendarIntegration(true);
        }
      }
    } catch (error) {
      console.error('Error checking calendar integration:', error);
    }
  };

  /* ──────────────────────────────────────────────────────
   * Core logic to persist substage + generate assessment
   * ──────────────────────────────────────────────────── */
  const saveNewSubstage = async (nameToUse = substageName) => {
    const iconClass = substageInfo.icon;
    const currentStage = getCurrentStage();
    if (!currentStage) throw new Error('Could not determine current stage');
    if (!userUID) throw new Error('User not authenticated');

    const substageId = `${substageType}-${Date.now()}`;
    const newSubstageData = {
      id: substageId,
      name: nameToUse,
      type: substageType,
      iconClass: iconClass,
      icon: `fa-solid ${iconClass}`,
      title: nameToUse,
      link: '',
      afterId: afterSubstageId || null
    };

    /* ------------- AI assessment generation ------------ */
    if (substageType === 'ai-assessment') {
      try {
        const companySnap = await get(ref(database, `companies/${userUID}/description`));
        const companyDescription = companySnap.exists() ? companySnap.val() : '';

        setIsGenerating(true);
        const resp = await fetch(`${BACKEND_URL}/generate-assessment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: substageName,
            assessment_type: assessmentType,
            // This legacy flow has no explicit job selection. Never use the
            // account's first job as an implicit context (which previously made
            // previews look like whichever job happened to sort first).
            job_description: '',
            company_description: companyDescription,
            num_questions: numQuestions,
            config: config                              // NEW
          })
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.error || 'Assessment generation failed');

        await set(ref(database, `companies/${userUID}/assessments/${data.assessment_id}`), data.assessment);
        newSubstageData.link = data.assessment_id;
      } catch (err) {
        console.error('AI assessment generation error:', err);
        alert(uiText('Failed to generate assessment. Proceeding with empty assessment.'));
      } finally {
        setIsGenerating(false);
      }
    }

    await set(
      ref(database, `companies/${userUID}/hiringWorkflow/${currentStage}/substages/${substageId}`),
      newSubstageData
    );
    return newSubstageData;
  };

  const getCurrentStage = () => currActiveStage;

  /* ──────────────────────────────────────────────────────
   * JSX helpers for type-specific extra fields
   * ──────────────────────────────────────────────────── */
  const renderExtraFields = () => {
    const inputStyle = {
      width: '80%',
      padding: '10px 15px',
      borderRadius: '12px',
      backgroundColor: '#F0F0F0',
      border: 'none',
      fontFamily: 'Montserrat',
      fontSize: '16px',
      outline: 'none'
    };

    const selectStyle = {
      padding: '10px 15px',
      borderRadius: '12px',
      backgroundColor: '#F0F0F0',
      border: 'none',
      fontFamily: 'Montserrat',
      fontSize: '16px',
      outline: 'none'
    };

    const textareaStyle = {
      ...inputStyle,
      minHeight: '80px',
      resize: 'vertical'
    };

    switch (assessmentType) {
      case 'sjt':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Common workplace scenarios to test (comma-separated):")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Examples: handling difficult customers, managing conflicting priorities, ethical dilemmas")}</small>
              <textarea
                value={config.dilemmas || ''}
                onChange={(e) => updateConfig('dilemmas', e.target.value)}
                placeholder={uiText("handling difficult customers, managing team conflicts, ethical business decisions, time management challenges")}
                style={textareaStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Company values to emphasize in scenarios:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What core values should candidates demonstrate through their responses?")}</small>
              <input
                value={config.values || ''}
                onChange={(e) => updateConfig('values', e.target.value)}
                placeholder={uiText("integrity, teamwork, customer focus, innovation, accountability")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Position level for scenario complexity:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Higher levels get more complex leadership and strategic scenarios")}</small>
              <select
                value={config.seniority || ''}
                onChange={(e) => updateConfig('seniority', e.target.value)}
                style={selectStyle}
              >
                <option value="">{uiText("Select position level…")}</option>
                <option value="IC">{uiText("Individual Contributor (Entry to Mid-level)")}</option>
                <option value="Lead">{uiText("Team Lead (Senior with some leadership)")}</option>
                <option value="Manager">{uiText("Manager (Full management responsibilities)")}</option>
              </select>
            </div>
          </>
        );
      case 'work_sample':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Expected deliverable format:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What should candidates create or deliver? Be specific about the format.")}</small>
              <input
                value={config.tasks || ''}
                onChange={(e) => updateConfig('tasks', e.target.value)}
                placeholder={uiText("PowerPoint presentation, Figma design mockup, SQL query results, written strategy document")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Tools or software to include:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What tools, software, or platforms should candidates use or demonstrate knowledge of?")}</small>
              <input
                value={config.tools || ''}
                onChange={(e) => updateConfig('tools', e.target.value)}
                placeholder={uiText("Excel, PowerPoint, Figma, SQL, Python, Tableau, Salesforce")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'data_insight':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Data types to focus on:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What types of data should candidates analyze?")}</small>
              <input
                value={config.data_types || ''}
                onChange={(e) => updateConfig('data_types', e.target.value)}
                placeholder={uiText("customer data, sales metrics, financial reports, user analytics, market research")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Key metrics to analyze:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What specific metrics or KPIs should be the focus of the analysis?")}</small>
              <input
                value={config.metrics || ''}
                onChange={(e) => updateConfig('metrics', e.target.value)}
                placeholder={uiText("conversion rates, customer lifetime value, churn rate, revenue growth, user engagement")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'video_pitch':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Target audience for the presentation:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Who should candidates imagine they're presenting to?")}</small>
              <input
                value={config.audience || ''}
                onChange={(e) => updateConfig('audience', e.target.value)}
                placeholder={uiText("executive team, potential investors, client stakeholders, board of directors")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Product, service, or concept to present:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What should candidates be pitching or presenting about?")}</small>
              <input
                value={config.presentation_types || ''}
                onChange={(e) => updateConfig('presentation_types', e.target.value)}
                placeholder={uiText("new mobile app, marketing campaign, business strategy, product feature")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'mcq_core':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Industry regulations or knowledge framework to test:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What specific industry knowledge, regulations, or standards should be covered?")}</small>
              <input
                value={config.topics || ''}
                onChange={(e) => updateConfig('topics', e.target.value)}
                placeholder={uiText("GDPR privacy compliance, HIPAA healthcare regulations, financial services regulations, industry best practices")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Difficulty level:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What difficulty level should the questions be?")}</small>
              <select
                value={config.difficulty || ''}
                onChange={(e) => updateConfig('difficulty', e.target.value)}
                style={selectStyle}
              >
                <option value="">{uiText("Select difficulty…")}</option>
                <option value="basic">{uiText("Basic - Entry level knowledge")}</option>
                <option value="intermediate">{uiText("Intermediate - Mid-level expertise")}</option>
                <option value="advanced">{uiText("Advanced - Expert level knowledge")}</option>
              </select>
            </div>
          </>
        );
      case 'coding_sandbox':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Primary programming languages (comma-separated):")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Which programming languages should the assessment focus on?")}</small>
              <input
                value={config.languages || ''}
                onChange={(e) => updateConfig('languages', e.target.value)}
                placeholder={uiText("Python, JavaScript, Java, C++, SQL")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Algorithm complexity focus:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Test knowledge of Big O notation, time/space complexity optimization")}</small>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={!!config.focusComplexity}
                  onChange={(e) => updateConfig('focusComplexity', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span>{uiText("Include algorithm complexity questions")}</span>
              </div>
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Allowed libraries and frameworks (optional):")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Specific libraries candidates can use, or leave blank for standard libraries only")}</small>
              <input
                value={config.allowedLibs || ''}
                onChange={(e) => updateConfig('allowedLibs', e.target.value)}
                placeholder={uiText("Pandas, NumPy, React, Express.js, TensorFlow")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'personality':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Key personality traits to evaluate:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Which personality characteristics are most important for this role?")}</small>
              <input
                value={config.traits || ''}
                onChange={(e) => updateConfig('traits', e.target.value)}
                placeholder={uiText("conscientiousness, extroversion, openness to experience, emotional stability, agreeableness")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Work style preferences to assess:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What work environment and style preferences should be evaluated?")}</small>
              <input
                value={config.work_style || ''}
                onChange={(e) => updateConfig('work_style', e.target.value)}
                placeholder={uiText("team collaboration, independent work, structured environment, creative freedom")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'behavioral':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Behavioral competencies to assess:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Which specific behaviors and competencies are most important for this role?")}</small>
              <input
                value={config.competencies || ''}
                onChange={(e) => updateConfig('competencies', e.target.value)}
                placeholder={uiText("leadership, teamwork, adaptability, problem-solving, communication, initiative")}
                style={inputStyle}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Situational contexts to include:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What types of situations should candidates be evaluated on?")}</small>
              <input
                value={config.situations || ''}
                onChange={(e) => updateConfig('situations', e.target.value)}
                placeholder={uiText("conflict resolution, change management, team leadership, crisis handling")}
                style={inputStyle}
              />
            </div>
          </>
        );
      case 'cognitive':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Cognitive areas to test:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Which cognitive abilities should be emphasized in this assessment?")}</small>
              <select
                value={config.cognitive_areas || ''}
                onChange={(e) => updateConfig('cognitive_areas', e.target.value)}
                style={selectStyle}
              >
                <option value="">{uiText("Select cognitive areas…")}</option>
                <option value="numerical">{uiText("Numerical - Math, statistics, data interpretation")}</option>
                <option value="verbal">{uiText("Verbal - Language, communication, reading comprehension")}</option>
                <option value="logical">{uiText("Logical - Problem-solving, pattern recognition, reasoning")}</option>
                <option value="spatial">{uiText("Spatial - Visual reasoning, pattern recognition")}</option>
                <option value="comprehensive">{uiText("Comprehensive - All cognitive areas")}</option>
              </select>
            </div>

            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Difficulty level:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("What difficulty level should the cognitive questions be?")}</small>
              <select
                value={config.difficulty_level || ''}
                onChange={(e) => updateConfig('difficulty_level', e.target.value)}
                style={selectStyle}
              >
                <option value="">{uiText("Select difficulty…")}</option>
                <option value="basic">{uiText("Basic - Entry level")}</option>
                <option value="intermediate">{uiText("Intermediate - Mid-level")}</option>
                <option value="advanced">{uiText("Advanced - Senior level")}</option>
              </select>
            </div>
          </>
        );
      case 'mixed':
        return (
          <>
            <div className="input-group">
              <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>{uiText("Assessment components and weightings:")}</label>
              <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Define which assessment types to combine and their relative importance. Use format: type:percentage")}</small>
              <input
                value={config.componentsWeight || ''}
                onChange={(e) => updateConfig('componentsWeight', e.target.value)}
                placeholder="sjt:40%, work_sample:30%, mcq_core:30% OR personality:30%, behavioral:40%, cognitive:30%"
                style={inputStyle}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  /* ──────────────────────────────────────────────────────
   * Render
   * ──────────────────────────────────────────────────── */
  return (
    <>
      {CALENDAR_ENABLED && showCalendarIntegration && (
        <CalendarIntegration
          userUID={userUID}
          onClose={() => setShowCalendarIntegration(false)}
          onConnectionSuccess={() => {
            setShowCalendarIntegration(false);
            alert('Calendar integration successful! You can now schedule interviews automatically.');
          }}
        />
      )}

      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="modal-header">
            <h2>{t(`addSubstage.add${substageType === 'ai-assessment' ? 'AIAssessment' : substageType === 'interview' ? 'Interview' : substageType === 'filtering' ? 'Filtering' : 'Assessment'}`)}</h2>
            <div className="close" onClick={onClose}>&times;</div>
          </div>

          <div className="modal-page-container">
            <div className="form-section">
              <div className="substage-popup-header" style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <i className={`fa-solid ${substageInfo.icon}`} style={{ color: '#568ded', fontSize: '24px' }}></i>
                <p style={{
                  fontFamily: isRTL ? 'Alexandria, Montserrat, sans-serif' : 'Montserrat, sans-serif',
                  color: "var(--app-neutral-ink, #000)",
                  fontSize: '24px',
                  marginInlineStart: '10px',
                  marginBottom: '0'
                }}>
                  {t('addSubstage.configuration')}
                </p>
              </div>

              <p style={{ margin: '15px 0', fontSize: '18px' }}>
                {t('addSubstage.enterName')}
              </p>

              <div className="input-group">
                <input
                  value={substageName}
                  onChange={(e) => setSubstageName(e.target.value)}
                  placeholder={t('addSubstage.namePlaceholder')}
                  readOnly={substageType === 'meet-the-team-and-assign-buddy' || substageType === 'first-week-checklist'}
                  style={{
                    backgroundColor: (substageType === 'meet-the-team-and-assign-buddy' || substageType === 'first-week-checklist') ? '#f8f9fa' : '#F0F0F0',
                    border: 'none',
                    marginTop: '15px',
                    width: '100%',
                    padding: '10px 15px',
                    borderRadius: '12px',
                    color: (substageType === 'meet-the-team-and-assign-buddy' || substageType === 'first-week-checklist') ? '#666' : '#000',
                    fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
                    outline: 'none',
                    fontSize: '16px',
                    cursor: (substageType === 'meet-the-team-and-assign-buddy' || substageType === 'first-week-checklist') ? 'not-allowed' : 'text'
                  }}
                />
                {substageType === 'meet-the-team-and-assign-buddy' && (
                  <p style={{
                    fontSize: '12px',
                    color: '#666',
                    margin: '5px 0 0 0',
                    fontStyle: 'italic'
                  }}>{uiText("The name for this substage is automatically set to \"Meet the Team\"")}</p>
                )}
                {substageType === 'first-week-checklist' && (
                  <p style={{
                    fontSize: '12px',
                    color: '#666',
                    margin: '5px 0 0 0',
                    fontStyle: 'italic'
                  }}>{uiText("The name for this substage is automatically set to \"First Week Checklist\"")}</p>
                )}
              </div>

              {substageType === 'ai-assessment' && (
                <>
                  <div className="input-group">
                    <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>
                      {t('addSubstage.selectType')}
                    </label>
                    <select
                      value={assessmentType}
                      onChange={(e) => {
                        setAssessmentType(e.target.value);
                        setConfig({}); // reset config on type change
                      }}
                      style={{
                        padding: '10px 15px',
                        borderRadius: '12px',
                        width: '100%',
                        backgroundColor: '#F0F0F0',
                        border: 'none',
                        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    >
                      <option value="">{t('addSubstage.selectTypePlaceholder')}</option>
                      {assessmentOptions.map((opt) => (
                        <option
                          key={opt.value}
                          value={opt.disabled ? "" : opt.value}
                          disabled={opt.disabled}
                          style={{
                            color: opt.disabled ? '#999' : '#000',
                            fontStyle: opt.disabled ? 'italic' : 'normal'
                          }}
                        >
                          {uiText(opt.label)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {assessmentType && (
                    <>
                      <div className="input-group">
                        <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>
                          {t('addSubstage.timeLimit')}
                        </label>
                        <small style={{ color: '#666', marginBottom: '10px', display: 'block' }}>{uiText("Set a high number (e.g., 999) for untimed assessments. Most assessments work well with 30-90 minutes.")}</small>
                        <input
                          type="number"
                          value={config.duration || ''}
                          onChange={(e) => updateConfig('duration', e.target.value)}
                          placeholder="60"
                          min="1"
                          style={{
                            padding: '10px 15px',
                            borderRadius: '12px',
                            width: '150px',
                            backgroundColor: '#F0F0F0',
                            border: 'none',
                            fontFamily: 'Montserrat',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                        />
                      </div>

                      {renderExtraFields()}

                      <div className="input-group">
                        <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>
                          {t('addSubstage.insertAfter')}
                        </label>
                        <select
                          value={afterSubstageId}
                          onChange={(e) => setAfterSubstageId(e.target.value)}
                          style={{
                            padding: '10px 15px',
                            borderRadius: '12px',
                            width: '100%',
                            backgroundColor: '#F0F0F0',
                            border: 'none',
                            fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                        >
                          <option value="">{t('addSubstage.atEnd')}</option>
                          {existingSubstages.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.title || s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group">
                        <label style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontWeight: "var(--app-weight-strong, bold)" }}>
                          {t('addSubstage.numQuestions')}
                        </label>
                        <input
                          type="number"
                          value={numQuestions}
                          onChange={(e) => setNumQuestions(parseInt(e.target.value) || 5)}
                          min="1"
                          max="20"
                          style={{
                            padding: '10px 15px',
                            borderRadius: '12px',
                            width: '150px',
                            backgroundColor: '#F0F0F0',
                            border: 'none',
                            fontFamily: 'Montserrat',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              onClick={handleSave}
              className="btn-primary"
            >
              {t('addSubstage.save')}
            </button>
          </div>

          {/* Loading overlay - now covers entire modal */}
          {isGenerating && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255,255,255,0.9)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                borderRadius: '12px'
              }}
            >
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '32px', color: '#568DED', marginBottom: '16px' }}></i>
              <p style={{
                fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
                fontSize: '16px',
                color: '#568DED',
                margin: 0,
                fontWeight: '600'
              }}>
                {t('addSubstage.generating')}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddSubstagePopup;
